import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Image,
  Platform,
  ScrollView,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { AppScreens } from "../../../../../screens/Utility/AppScreens";
import { navigateToScreen } from "../../../../../utility/NavigationHelper";
import { useGoLive } from "../GoLiveContext";
import { AppImages } from "../../../../../utility/AppImages";
import { useWsProvider } from "../../../../../context/NativeApplicationContext";
import { toast } from "react-toastify";
import Separator from "../../../../../Components/Separator";
import { getLoginData } from "../../../../../utility/AppStoreData";
import { useStreamingOAuth } from "../../../../../hooks/useStreamingOAuth";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../../../../utility/AppColors";
import { SwitchCommon } from "../../../../../common/SwitchCommon";
import { AppFontStyle } from "../../../../../styles/AppFontStyle";
import { streamService } from "../../../../../services/streamService";
import { useStreamStatus } from "../../../../../context/StreamStatusContext";

interface SetupState {
  isEntering: boolean;
  enterStatus: string;
  isConnecting: boolean;
  isDownloading: boolean;
  downloadProgress: number;
  isDownloaded: boolean;
}

export const Step1StreamForm = () => {
  const navigation = useNavigation();
  const {
    streamData,
    setStreamData,
    setStep,
    isLoading: contextLoading,
  } = useGoLive();
  const { isNativeAppConnected, setStreamConfig, connectWithDeeplink } =
    useWsProvider();
  const { createStream, refreshStatus } = useStreamStatus();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [os, setOs] = useState<"mac" | "windows" | "ios" | "android" | null>(
    null
  );
  const [isCreatingStream, setIsCreatingStream] = useState(false);

  const [setupState, setSetupState] = useState<SetupState>({
    isEntering: false,
    enterStatus: "",
    isConnecting: false,
    isDownloading: false,
    downloadProgress: 0,
    isDownloaded:
      Platform.OS === "web" &&
      !!localStorage.getItem("isKOBroadcasterDownloaded"),
  });

  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch Access Token
  React.useEffect(() => {
    getLoginData().then((data) => {
      if (data && isMountedRef.current) {
        setAccessToken(data.AccessToken);
      }
    });
  }, []);

  // Use Streaming OAuth Hook
  const {
    isYouTubeConnected,
    isTwitchConnected,
    redirectToYouTubeOAuth,
    redirectToTwitchOAuth,
    loadingState,
    twitchUsername,
    youtubeUsername,
    fetchStatus,
  } = useStreamingOAuth(accessToken || "", "/go-live");

  // Initial Platform Status Fetch
  React.useEffect(() => {
    if (accessToken) {
      fetchStatus();
    }
  }, [accessToken]);

  const updateSetupState = React.useCallback(
    (key: keyof SetupState, value: any) => {
      if (!isMountedRef.current) return;
      setSetupState((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  // OS Detection
  React.useEffect(() => {
    if (Platform.OS === "web") {
      const p = (navigator.platform || "").toLowerCase();
      const ua = (navigator.userAgent || "").toLowerCase();
      if (p.includes("mac") || ua.includes("mac")) setOs("mac");
      else if (p.includes("win") || ua.includes("win")) setOs("windows");
      else setOs(null);
    } else {
      setOs(Platform.OS as "ios" | "android");
    }
  }, []);

  const macS3Link =
    "https://broadcaster.kallout.io/KO+Broadcaster-1.0.5-arm64.dmg";
  const windowsS3Link =
    "https://broadcaster.kallout.io/KO+Broadcaster+Setup+1.0.5.exe";
  const downloadLink =
    os === "mac" ? macS3Link : os === "windows" ? windowsS3Link : null;

  const thumbnailPreviewUrl = React.useMemo(() => {
    if (streamData.thumbnail) {
      return URL.createObjectURL(streamData.thumbnail);
    }
    return null;
  }, [streamData.thumbnail]);

  const handleConnectToBroadcaster = React.useCallback(() => {
    if (Platform.OS !== "web") {
      toast.error(
        "Please use a desktop (macOS/Windows) to install the broadcaster."
      );
      return;
    }
    if (!downloadLink) {
      toast.error("Unsupported operating system");
      return;
    }

    updateSetupState("isConnecting", true);
    updateSetupState("isDownloading", true);
    updateSetupState("downloadProgress", 0);

    const xhr = new XMLHttpRequest();
    xhr.open("GET", downloadLink, true);
    xhr.responseType = "blob";

    xhr.onprogress = (event) => {
      if (event.lengthComputable) {
        updateSetupState(
          "downloadProgress",
          Math.round((event.loaded / event.total) * 100)
        );
      }
    };

    xhr.onload = () => {
      updateSetupState("isConnecting", false);
      updateSetupState("isDownloading", false);

      if (xhr.status === 200) {
        const blob = xhr.response;
        const fileName =
          os === "mac"
            ? "KO Broadcaster-1.0.5-arm64.dmg"
            : "KO Broadcaster Setup 1.0.5.exe";
        const linkElement = document.createElement("a");
        linkElement.href = URL.createObjectURL(blob);
        linkElement.download = fileName;
        document.body.appendChild(linkElement);
        linkElement.click();
        document.body.removeChild(linkElement);
        URL.revokeObjectURL(linkElement.href);

        localStorage.setItem("isKOBroadcasterDownloaded", "true");
        localStorage.setItem(
          "koBroadcasterDownloadTime",
          new Date().toISOString()
        );
        updateSetupState("isDownloaded", true);
        toast.success("Download complete. Install and run the KO Broadcaster.");
      } else {
        toast.error("Failed to download the application.");
      }
    };

    xhr.onerror = () => {
      updateSetupState("isConnecting", false);
      updateSetupState("isDownloading", false);
      toast.error("Error occurred during download.");
    };

    xhr.send();
  }, [downloadLink, os, updateSetupState]);

  // Handle Platform Toggle (Local State Only since Event isn't created yet)
  // Handle Platform Toggle (Local State Only since Event isn't created yet)
  const handlePlatformToggle = (platform: "twitch" | "youtube") => {
    const isConnected =
      platform === "twitch" ? isTwitchConnected : isYouTubeConnected;

    if (!isConnected) return; // Cannot toggle on if not connected

    setStreamData((prev) => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platform]: !prev.platforms[platform],
      },
    }));
  };

  // Sync StreamData with Connection Status (Auto-disable if disconnected)
  React.useEffect(() => {
    if (!isTwitchConnected && streamData.platforms.twitch) {
      setStreamData((prev) => ({
        ...prev,
        platforms: { ...prev.platforms, twitch: false },
      }));
    }
    if (!isYouTubeConnected && streamData.platforms.youtube) {
      setStreamData((prev) => ({
        ...prev,
        platforms: { ...prev.platforms, youtube: false },
      }));
    }
  }, [isTwitchConnected, isYouTubeConnected]);

  const renderPlatformStatus = (isConnected: boolean) => {
    if (loadingState.Twitch || loadingState.YouTube) {
      return (
        <Text style={[AppFontStyle.SEMIBOLD_ANYBODY_12, { color: "#FFA500" }]}>
          Checking connection...
        </Text>
      );
    }
    return (
      <Text
        style={[
          AppFontStyle.SEMIBOLD_ANYBODY_12,
          { color: isConnected ? "#409101" : "#C3081B" },
        ]}
      >
        {isConnected ? "Connected" : "Disconnected"}
      </Text>
    );
  };

  const renderActionButton = (
    isConnected: boolean,
    onConnect: () => void,
    switchComponent: JSX.Element,
    platform: string
  ) => {
    const isLoading = false; // Simplified for this component

    if (isConnected) {
      return (
        <View style={{ opacity: isLoading ? 0.6 : 1 }}>
          {switchComponent}
          {isLoading && (
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_10,
                { color: "#FFA500", marginTop: 2 },
              ]}
            >
              Updating...
            </Text>
          )}
        </View>
      );
    }

    return (
      <TouchableOpacity
        style={[
          styles.btnStyle,
          {
            backgroundColor: AppColors.appViolentColor_6D08C3,
            alignItems: "center",
            justifyContent: "center",
            height: 40,
            width: 100,
            borderRadius: 6,
          },
        ]}
        onPress={onConnect}
      >
        <Text style={{ color: "white" }}>Connect</Text>
      </TouchableOpacity>
    );
  };

  const isStreamPlatformSetupCompleted =
    Number(streamData.platforms.youtube) + Number(streamData.platforms.twitch) >
    0;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.container}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          paddingVertical: 10,
        }}
      >
        <LinearGradient
          colors={["#1F2937", "#111827"]}
          start={[0, 0]}
          end={[1, 1]}
          style={{
            width: 40,
            height: 40,
            padding: 6,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            justifyContent: "center",
            alignItems: "center",
            aspectRatio: 1,
          }}
        >
          <TouchableOpacity
            onPress={() => navigateToScreen(navigation, AppScreens.HomeScreen)}
            style={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Image
              source={AppImages.icArrowLeft}
              style={{ width: 24, height: 24 }}
              resizeMode="contain"
            />
          </TouchableOpacity>
        </LinearGradient>
        <Text style={styles.headerTitle}>Create live Stream</Text>
      </View>
      <View style={styles.divider} />

      <Text style={styles.sectionTitle}>Stream Details</Text>

      {/* Stream Name */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Stream Name</Text>
        <TextInput
          style={styles.textInput}
          placeholder="Enter stream name"
          placeholderTextColor="#6B7280"
          value={streamData.streamName}
          onChangeText={(text) =>
            setStreamData((prev) => ({ ...prev, streamName: text }))
          }
        />
      </View>

      {/* Description & Thumbnail Row */}
      <View style={{ flexDirection: "row", gap: 12 }}>
        {/* Stream Description */}
        <View style={[styles.inputGroup, { flex: 1 }]}>
          <Text style={styles.label}>Description</Text>
          <TextInput
            style={[styles.textInput, styles.textArea]}
            placeholder="Enter stream description"
            placeholderTextColor="#6B7280"
            multiline
            numberOfLines={4}
            value={streamData.description}
            onChangeText={(text) =>
              setStreamData((prev) => ({ ...prev, description: text }))
            }
          />
        </View>

        {/* Upload Thumbnail */}
        <View style={[styles.inputGroup, { flex: 1 }]}>
          <Text style={styles.label}>Thumbnail (Optional)</Text>
          <TouchableOpacity
            style={styles.uploadBox}
            onPress={() => {
              if (!streamData.thumbnail) {
                const fileInput = document.getElementById("thumbnailInput");
                if (fileInput) fileInput.click();
              }
            }}
            activeOpacity={streamData.thumbnail ? 1 : 0.7}
          >
            {streamData.thumbnail ? (
              <View
                style={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <Image
                  source={{ uri: thumbnailPreviewUrl || "" }}
                  style={{ width: "80%", height: "80%", borderRadius: 4 }}
                  resizeMode="contain"
                />
                <TouchableOpacity
                  style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    borderRadius: 12,
                    padding: 4,
                  }}
                  onPress={(e) => {
                    e.stopPropagation();
                    setStreamData((prev) => ({ ...prev, thumbnail: null }));
                  }}
                >
                  <Text
                    style={{ color: "white", fontSize: 10, fontWeight: "bold" }}
                  >
                    ✕
                  </Text>
                </TouchableOpacity>
              </View>
            ) : (
              <>
                <Text style={{ color: "#6B7280", fontSize: 14 }}>
                  Click to upload thumbnail
                </Text>
                <View style={styles.browseButton}>
                  <Text style={styles.browseButtonText}>Browse</Text>
                </View>
              </>
            )}
          </TouchableOpacity>
          <input
            type="file"
            id="thumbnailInput"
            style={{ display: "none" }}
            accept="image/png, image/jpeg, image/jpg"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setStreamData((prev) => ({ ...prev, thumbnail: file }));
              }
            }}
          />
          <Text style={styles.subText}>Only JPG, PNG are allowed.</Text>
        </View>
      </View>

      <View>
        {/* Connect Streaming Platform Header UI from StreamView */}
        <LinearGradient
          colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
          start={[0, 0]}
          end={[1, 1]}
          style={{
            padding: 20,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 10,
              gap: 10,
            }}
          >
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                backgroundColor: isStreamPlatformSetupCompleted
                  ? AppColors.green_00B536
                  : "#DC2626",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isStreamPlatformSetupCompleted ? (
                <Image
                  source={AppImages.icComplete}
                  style={{ width: 18, height: 18 }}
                />
              ) : (
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_20,
                    { color: AppColors.white, fontSize: 14 },
                  ]}
                >
                  1
                </Text>
              )}
            </View>
            <Text style={[AppFontStyle.BOLD_ANYBODY_20]}>
              Connect Streaming Platform
            </Text>
          </View>

          {/* Platform cards container */}
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
            }}
          >
            {/* Twitch Card */}
            <View
              style={{
                backgroundColor: isTwitchConnected
                  ? "rgba(220, 38, 38, 0.20)"
                  : "#1F1C1C",
                borderColor: "#DC2626",
                borderWidth: 1,
                borderRadius: 4,
                padding: 10,
                width: "50%",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 30 }}
              >
                <Image
                  source={AppImages.icTwitchLogo}
                  style={{ width: 48, height: 48 }}
                />
                <View>
                  <Text style={AppFontStyle.BOLD_ANYBODY_18}>Twitch</Text>
                  {renderPlatformStatus(isTwitchConnected)}
                  {twitchUsername && (
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: AppColors.white, marginTop: 4, opacity: 0.8 },
                      ]}
                    >
                      @{twitchUsername}
                    </Text>
                  )}
                </View>
              </View>
              <View
                style={{ alignItems: "flex-start", justifyContent: "center" }}
              >
                {renderActionButton(
                  isTwitchConnected,
                  redirectToTwitchOAuth,
                  <SwitchCommon
                    switchState={streamData.platforms.twitch}
                    setSwitchState={() => handlePlatformToggle("twitch")}
                    color="red"
                  />,
                  "Twitch"
                )}
              </View>
            </View>

            {/* YouTube Card */}
            <View
              style={{
                backgroundColor: isYouTubeConnected
                  ? "rgba(220, 38, 38, 0.20)"
                  : "#1F1C1C",
                borderColor: "#DC2626",
                borderWidth: 1,
                borderRadius: 4,
                padding: 10,
                width: "50%",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 30 }}
              >
                <Image
                  source={AppImages.icYoutubeLogo}
                  style={{ width: 48, height: 48 }}
                />
                <View>
                  <Text style={AppFontStyle.BOLD_ANYBODY_18}>YouTube</Text>
                  {renderPlatformStatus(isYouTubeConnected)}
                  {youtubeUsername && (
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: AppColors.white, marginTop: 4, opacity: 0.8 },
                      ]}
                    >
                      @{youtubeUsername}
                    </Text>
                  )}
                </View>
              </View>
              <View
                style={{ alignItems: "flex-start", justifyContent: "center" }}
              >
                {renderActionButton(
                  isYouTubeConnected,
                  redirectToYouTubeOAuth,
                  <SwitchCommon
                    switchState={streamData.platforms.youtube}
                    setSwitchState={() => handlePlatformToggle("youtube")}
                    color="red"
                  />,
                  "YouTube"
                )}
              </View>
            </View>
          </View>
        </LinearGradient>

        {/* Broadcaster Connection Section UI from StreamView */}
        <LinearGradient
          colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
          start={[0, 0]}
          end={[1, 1]}
          style={{
            marginTop: 10,
            padding: 20,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
          }}
        >
          {/* Header Row */}
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 10,
              gap: 10,
            }}
          >
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                backgroundColor: setupState.isConnecting
                  ? "#FFA500"
                  : isNativeAppConnected
                  ? AppColors.green_00B536
                  : "#DC2626",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {setupState.isConnecting ? (
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_20,
                    { color: AppColors.white, fontSize: 14 },
                  ]}
                >
                  ?
                </Text>
              ) : isNativeAppConnected ? (
                <Image
                  source={AppImages.CheckInTick}
                  style={{ width: 18, height: 18 }}
                />
              ) : (
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_20,
                    { color: AppColors.white, fontSize: 14 },
                  ]}
                >
                  2
                </Text>
              )}
            </View>
            <Text style={[AppFontStyle.BOLD_ANYBODY_20]}>
              Broadcaster Software connection
            </Text>

            {/* Manual Connect Button */}
            {!setupState.isConnecting &&
              !setupState.isDownloading &&
              !isNativeAppConnected && (
                <TouchableOpacity
                  style={{
                    display: "flex",
                    height: 40,
                    paddingVertical: 10,
                    paddingHorizontal: 24,
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 10,
                    borderRadius: 6,
                    backgroundColor: "#09F",
                    marginLeft: "auto",
                    opacity: setupState.isConnecting ? 0.7 : 1,
                  }}
                  onPress={() => {
                    connectWithDeeplink();
                  }}
                  disabled={setupState.isConnecting}
                >
                  <Text
                    style={{ color: "#FFF", fontWeight: "600", fontSize: 14 }}
                  >
                    {setupState.isConnecting ? "Connecting..." : "Connect"}
                  </Text>
                </TouchableOpacity>
              )}
          </View>

          <Separator
            color={AppColors.white}
            height={1}
            marginTop={10}
            marginBottom={10}
            marginHorizontal={0}
          />

          {/* Status Box */}
          <View
            style={{
              gap: 10,
              padding: 10,
              marginTop: 10,
              paddingVertical: 20,
              backgroundColor: isNativeAppConnected ? "#86EFAC33" : "#C3081B33",
              borderRadius: 5,
              borderWidth: 1,
              borderColor: isNativeAppConnected ? "#86EFAC" : "#C3081B",
              minHeight: 80,
              justifyContent: "center",
            }}
          >
            {setupState.isDownloading ? (
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text
                  style={[
                    AppFontStyle.SEMIBOLD_ANYBODY_14,
                    { color: AppColors.blue_233DFF },
                  ]}
                >
                  KO Broadcaster downloading
                </Text>
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_14,
                    { color: AppColors.blue_233DFF },
                  ]}
                >
                  {setupState.downloadProgress}%
                </Text>
              </View>
            ) : (
              <>
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_14,
                    { color: isNativeAppConnected ? "#86EFAC" : "#C3081B" },
                  ]}
                >
                  {isNativeAppConnected
                    ? `KO Broadcaster is Connected - idle`
                    : "KO Broadcaster is Disconnected"}
                </Text>
                {isNativeAppConnected && (
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      { color: AppColors.white, marginTop: 5 },
                    ]}
                  >
                    🎉 Awesome! We'll handle all your streaming automatically.
                  </Text>
                )}
              </>
            )}
          </View>

          {/* Download Logic UI */}
          {!setupState.isConnecting && !isNativeAppConnected && (
            <View style={{ marginTop: 0 }}>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: 15.9,
                  fontWeight: "500",
                  lineHeight: 24,
                  marginBottom: 8,
                }}
              >
                KO Broadcaster is our official streaming software
              </Text>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: 14,
                  fontWeight: "500",
                  lineHeight: 24,
                }}
              >
                It only takes 2 minutes to download.
              </Text>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: 14,
                  fontWeight: "500",
                  lineHeight: 24,
                }}
              >
                Download and install the KO Broadcaster, and we will handle all
                for you
              </Text>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: 14,
                  fontWeight: "500",
                  lineHeight: 24,
                  marginBottom: 16,
                }}
              >
                If you already download it, click on connect button to connect
                it.
              </Text>

              <LinearGradient
                colors={["#DC2626", "#991B1B"]}
                start={[0, 0]}
                end={[1, 0]}
                style={{
                  display: "flex",
                  height: 40,
                  paddingVertical: 10,
                  paddingHorizontal: 24,
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 10,
                  borderRadius: 6,
                  alignSelf: "flex-end",
                }}
              >
                <TouchableOpacity
                  onPress={handleConnectToBroadcaster}
                  disabled={setupState.isDownloading}
                  style={{ opacity: setupState.isDownloading ? 0.7 : 1 }}
                >
                  <Text
                    style={{ color: "#FFF", fontWeight: "600", fontSize: 14 }}
                  >
                    {setupState.isDownloading
                      ? "Downloading..."
                      : "Download KO Broadcaster"}
                  </Text>
                </TouchableOpacity>
              </LinearGradient>
            </View>
          )}
        </LinearGradient>
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          marginTop: 40,
        }}
      >
        <TouchableOpacity
          style={[
            styles.createButton,
            {
              opacity:
                isCreatingStream ||
                !streamData.streamName ||
                !streamData.description ||
                !(
                  streamData.platforms.youtube || streamData.platforms.twitch
                ) ||
                !isNativeAppConnected
                  ? 0.5
                  : 1,
              backgroundColor:
                !streamData.streamName ||
                !streamData.description ||
                !(
                  streamData.platforms.youtube || streamData.platforms.twitch
                ) ||
                !isNativeAppConnected
                  ? "#374151"
                  : "#DC2626",
            },
          ]}
          onPress={async () => {
            if (isCreatingStream) return;

            if (!accessToken) {
              toast.error("You must be logged in to go live.");
              return;
            }

            if (!streamData.streamName) {
              toast.error("Please enter a stream name.");
              return;
            }

            setIsCreatingStream(true);
            try {
              // Use context's createStream to update state properly
              const stream = await createStream({
                title: streamData.streamName,
                description: streamData.description,
                platforms: {
                  youtube: streamData.platforms.youtube,
                  twitch: streamData.platforms.twitch,
                },
              });

              if (stream) {
                toast.success("Stream created successfully!");
                // Proceed to next step
                setStep(2);
              } else {
                toast.error("Failed to create stream");
              }
            } catch (error: any) {
              toast.error(error.message || "Failed to create stream");
            } finally {
              setIsCreatingStream(false);
            }
          }}
          disabled={
            isCreatingStream ||
            !streamData.streamName ||
            !streamData.description ||
            !(streamData.platforms.youtube || streamData.platforms.twitch) ||
            !isNativeAppConnected
          }
        >
          <Text style={styles.createButtonText}>
            {isCreatingStream ? "Creating..." : "Create stream"}
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    // backgroundColor: "transparent", // Handled by parent
    flexGrow: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#fff",
    marginBottom: 16,
  },
  divider: {
    height: 1,
    backgroundColor: "#374151",
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#D1D5DB",
    marginBottom: 12,
  },
  label: {
    fontSize: 14,
    color: "#9CA3AF",
    marginBottom: 8,
  },
  inputGroup: {
    marginBottom: 6,
  },
  textInput: {
    backgroundColor: "#1F2937",
    borderWidth: 1,
    borderColor: "#374151",
    borderRadius: 8,
    padding: 12,
    color: "#fff",
    fontSize: 14,
  },
  textArea: {
    height: 100,
    textAlignVertical: "top",
  },
  rowContainer: {
    flexDirection: "row",
  },
  uploadBox: {
    backgroundColor: "#1F2937",
    borderWidth: 1,
    borderColor: "#374151",
    borderRadius: 8,
    height: 100,
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    paddingHorizontal: 16,
  },
  browseButton: {
    borderWidth: 1,
    borderColor: "#DC2626",
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 16,
  },
  browseButtonText: {
    color: "#DC2626",
    fontWeight: "600",
    fontSize: 12,
  },
  subText: {
    color: "#6B7280",
    fontSize: 12,
    marginBottom: 16,
  },
  platformRow: {
    flexDirection: "row",
    gap: 20,
  },
  platformCard: {
    flex: 1,
    backgroundColor: "#111827",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#374151",
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  platformIcon: {
    width: 24,
    height: 24,
  },
  platformName: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 16,
  },
  platformStatus: {
    fontSize: 12,
  },
  connectButton: {
    backgroundColor: "#DC2626",
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 4,
  },
  connectButtonText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "600",
  },
  downloadButton: {
    backgroundColor: "#DC2626",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginLeft: 10,
  },
  downloadButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  createButton: {
    backgroundColor: "#DC2626",
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
  },
  createButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
  },
  // Added style to match StreamView wrapper if needed
  btnStyle: {
    // placeholder if needed
  },
});
